import React from 'react';
import { InfoCard, Link, Table, TableColumn, TableFilter } from '@backstage/core-components';
import { catalogApiRef, entityRouteRef, useEntity } from '@backstage/plugin-catalog-react';
import { GitHubActionResourceEntity } from '@eng-portal/czi-extensions-common';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { Entity, parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { Grid, Typography } from '@mui/material';
import { Chip } from '@material-ui/core';

export function GithubActionDependencyOfCard(): JSX.Element {
  const { entity: githubAction } = useEntity<GitHubActionResourceEntity>();
  const catalogApi = useApi(catalogApiRef);
  const entityRoute = useRouteRef(entityRouteRef);

  const { value, error, loading } = useAsync(async () => {
    const entityRefs: string[] = (githubAction.relations || []).
      filter(relation => relation.type === 'dependencyOf').
      map(relation => relation.targetRef);

    return catalogApi.getEntitiesByRefs({
      entityRefs,
    });
  }, [githubAction, catalogApi]);

  const title = "Repos using this GitHub Action";
  if (error) {
    return <InfoCard title={title}><div>Error loading dependencies: {error.message}</div></InfoCard>;
  }

  const columns: TableColumn[] = [
    {
      title: 'Name',
      field: 'metadata.title',
      width: '20%',
      render: (entity: any) => (
        <Link to={`${entityRoute(parseEntityRef(stringifyEntityRef(entity)))}`}>
          {entity.metadata.title}
        </Link>
      ),
    },
    {
      title: 'Description',
      field: 'metadata.description',
    },
    {
      title: 'Tags',
      field: 'metadata.tags',
      render: (entity: any) => (
        <Grid container>
          {(entity.metadata.tags || []).map((tag: string) => (
            <Grid item key={tag}>
              <Chip label={tag}/>
            </Grid>
          ))}
        </Grid>
      )
    },
  ];

  const filters: TableFilter[] = [
    {
      column: 'Tags',
      type: 'multiple-select',
    },
  ];

  const data = (value?.items || []).filter(item => !!item) as Entity[];

  return (
    <InfoCard title={title} noPadding>
      <Table
        isLoading={loading}
        data={data}
        columns={columns}
        filters={filters}
        emptyContent={(
          <Typography style={{ padding: 10 }}>
            No entities depend on this GitHub Action.
          </Typography>
        )}
        options={{
          pageSize: data.length,
          paging: false,
          padding: 'dense',
        }}
      />
    </InfoCard>
  );
}
